/* eslint-disable max-len */
import { OverallResolverBase } from './OverallResolverBase';
import { ComponentFactory, ViewContainerRef } from '@angular/core';
import { ConfigDataService } from '../services/config-data.service';
import { GraphDataService } from '../services/graph-data-service.service';
import { ViewPeriodService } from '../services/view-period.service';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { DynamicLineChartWrapperComponent } from '../shared-components/dynamic/dynamic-line-chart-wrapper/dynamic-line-chart-wrapper.component';
import { GenericLineChartData } from '../objects/chart';
import { PopoverController } from '@ionic/angular';
import * as moment from 'moment';
import { DynamicGraphAdditionalInput } from '../objects/config';
import { PIE_CHART_COLOR_LIST } from '../configs/color';
import { GraphDependency } from '../enum/graph-dependency.enum';
import { getAdditionalInput } from '../helpers/DynamicGraphHelper';
import { assertNullUndefined } from '../helpers/util';

export class OverallTrafficTrendResolver extends OverallResolverBase {

    public get graphDependencyMap(): { building: GraphDependency[]; floor: GraphDependency[]; zone: GraphDependency[] } {
        return {
            building: [GraphDependency.ENTRANCE_EXIT, GraphDependency.PREDICTION_ENTRANCE_EXIT],
            floor: [GraphDependency.ENTRANCE_EXIT_FLOOR, GraphDependency.PREDICTION_ENTRANCE_EXIT_FLOOR],
            zone: [GraphDependency.ENTRANCE_EXIT_ZONE, GraphDependency.PREDICTION_ENTRANCE_EXIT_ZONE]
        };
    }

    public async createComponent(
        componentFactory: ComponentFactory<unknown>,
        additionalInput: string | DynamicGraphAdditionalInput | undefined,
        configDataService: ConfigDataService,
        graphDataService: GraphDataService,
        _popoverController: PopoverController,
        viewPeriodService: ViewPeriodService,
        viewContainerRef: ViewContainerRef,
        subscription: Subscription
    ) {
        // load data here
        const level = getAdditionalInput(additionalInput, 'level') as DynamicGraphAdditionalInput['level'] | undefined;
        const groupData = getAdditionalInput(additionalInput, 'groupData') as DynamicGraphAdditionalInput['groupData'] | undefined;
        assertNullUndefined(level);
        this.processDynamicDependency(configDataService, graphDataService, level);
        // await configDataService.loadAppConfig();

        const initialTodayDate = moment().startOf(viewPeriodService.viewPeriod.toMomentString());
        type EntraceExitData = { entrance: number[]; exit: number[] };
        type FlattenEntraceExitData = { [name: string]: { entrance: number[]; exit: number[] } };
        const entranceExitReducer = (data: FlattenEntraceExitData, isEntrance: boolean) => {
            const channel = isEntrance ? 'entrance' : 'exit';
            return Object.entries(data).reduce((prev, [name, entranceExitData]) => {
                prev[name] = entranceExitData[channel];
                return prev;
            }, {} as { [name: string]: number[] });
        };
        const getTrafficTrendData = (
            isEntrance: boolean,
            dataBuilding: { [buildingName: string]: EntraceExitData },
            dataFloor: { [buildingName: string]: { [floorName: string]: EntraceExitData } },
            dataZone: { [buildingName: string]: { [floorName: string]: { [zoneName: string]: EntraceExitData } } },
        ) => entranceExitReducer(this.getLeveledData<EntraceExitData>(dataBuilding, dataFloor, dataZone, level), isEntrance);
        const initialComparingNames = level === 'building' ? this.getComparingNames(graphDataService.baseGraphData.comparingBuildingState$.value) : (level === 'floor' ? this.getComparingNames(graphDataService.baseGraphData.comparingFloorState$.value) : this.getComparingNames(graphDataService.baseGraphData.comparingZoneState$.value));
        const initialData = getTrafficTrendData(configDataService.isEntranceDataMode, graphDataService.buildingEntranceExitData$.value, graphDataService.entranceExitFloorData$.value, graphDataService.zoneEntranceExitFloorData$.value);
        const initialPredictData = getTrafficTrendData(configDataService.isEntranceDataMode, graphDataService.predictedBuildingTrafficNumber$.value, graphDataService.predictedFloorTrafficNumber$.value, graphDataService.predictedZoneTrafficNumber$.value);
        const initialColor: string[] = [];
        const chartData$ = new BehaviorSubject<GenericLineChartData[]>(Object.entries(initialData).sort().filter(([name, _data], idx) => {
            if (initialComparingNames.includes(name)) {
                if (level === 'floor' && configDataService.currentOrganization === `MBK`){
                    initialColor.push(configDataService.FLOOR_LINE_CHART_COLOR_LIST[name]);   
                } else if (level === 'zone' && configDataService.currentOrganization === `MBK`) {
                    initialColor.push(configDataService.ZONE_LINE_CHART_COLOR_LIST[name]); 
                } else {
                    initialColor.push(PIE_CHART_COLOR_LIST[idx % PIE_CHART_COLOR_LIST.length]);
                }
                return true;
            }
            return false;
        }).map(([name, data], idx) => ({
            points: data,
            prediction: !(initialPredictData[name]) ? [0] : initialPredictData[name],
            color: initialColor[idx],
            isLivePeriod: viewPeriodService.DAY_LIST_MOMENT.map(dateMoment => dateMoment.clone().isSameOrAfter(initialTodayDate.clone(), viewPeriodService.viewPeriod.toMomentCompareString())),
            toolTipLabel: viewPeriodService.DAY_LIST.map(dayText => `${this.getDisplayName(name)}, ${dayText}`),
            label: this.getDisplayName(name),
        })));

        // eslint-disable-next-line max-len
        subscription.add(combineLatest([configDataService.isEntranceDataMode$, graphDataService.buildingEntranceExitData$, graphDataService.entranceExitFloorData$, graphDataService.zoneEntranceExitFloorData$, combineLatest([graphDataService.predictedBuildingTrafficNumber$, graphDataService.predictedFloorTrafficNumber$, graphDataService.predictedZoneTrafficNumber$]),
            combineLatest([graphDataService.baseGraphData.comparingBuildingState$, graphDataService.baseGraphData.comparingFloorState$, graphDataService.baseGraphData.comparingZoneState$])]).subscribe(
            ([isEntranceDataMode, buildingEntranceExitData, entranceExitFloorData, entranceExitZoneData, [predictBuildingEntranceExitData, predictEntranceExitFloorData, predictEntranceExitZoneData], [comparingBuildingState, comparingFloorState, comparingZoneState]]) => {
                if ((!buildingEntranceExitData && level === 'building') || (!entranceExitFloorData && level === 'floor') || (!entranceExitZoneData && level === 'zone')) { return; }
                const todayDate = moment().startOf(viewPeriodService.viewPeriod.toMomentString());
                const comparingNames = level === 'building' ? this.getComparingNames(comparingBuildingState) : (level === 'floor' ? this.getComparingNames(comparingFloorState) : this.getComparingNames(comparingZoneState));
                const fullData = getTrafficTrendData(isEntranceDataMode, buildingEntranceExitData, entranceExitFloorData, entranceExitZoneData);
                const fullPredictionData = getTrafficTrendData(isEntranceDataMode, predictBuildingEntranceExitData, predictEntranceExitFloorData, predictEntranceExitZoneData);
                const chartColors: string[] = [];
                const diffDate = viewPeriodService.selectedDate.diff(todayDate, viewPeriodService.viewPeriod.toMomentCompareString());
                if (groupData) {
                    Object.keys(groupData).forEach(k => {
                        const arr2DOfDataPoints: number[][] = [];
                        const arr2DOfPredDataPoints: number[][] = [];
                        groupData[k].args.forEach(arg => {
                            arr2DOfDataPoints.push(fullData[arg]);
                            arr2DOfPredDataPoints.push(fullPredictionData[arg] || null);
                        });
                        fullData[k] = arr2DOfDataPoints.every(arr => arr === null) ? null :
                            groupData[k].oper === 'AVG' ? arr2DOfDataPoints.reduce((r, a) => r.map((b, i) => b !== null ? (a[i] + b) / a.length - 1 : null)) : arr2DOfDataPoints.reduce((r, a) => r.map((b, i) => b !== null ? a[i] + b : null));
                        fullPredictionData[k] = arr2DOfPredDataPoints.every(arr => arr === null) ? null :
                            groupData[k].oper === 'AVG' ? arr2DOfPredDataPoints.reduce((r, a) => r.map((b, i) => b !== null ? (a[i] + b) / a.length - 1 : null)) : arr2DOfPredDataPoints.reduce((r, a) => r.map((b, i) => b !== null ? a[i] + b : null));
                    });
                }
                const lineChartData: GenericLineChartData[] = Object.entries(fullData).sort().filter(([name, _data], idx) => {
                    if (comparingNames.includes(name)) {
                        if (level === 'floor' && configDataService?.FLOOR_LINE_CHART_COLOR_LIST){
                            chartColors.push(configDataService.FLOOR_LINE_CHART_COLOR_LIST[name]);   
                        } else if (level === 'zone' && configDataService?.ZONE_LINE_CHART_COLOR_LIST) {
                            chartColors.push(configDataService.ZONE_LINE_CHART_COLOR_LIST[name]); 
                        } else {
                            chartColors.push(PIE_CHART_COLOR_LIST[idx % PIE_CHART_COLOR_LIST.length]);
                        }
                        return true;
                    }
                    return false;
                }).map(([name, data], idx) => ({
                    points: data,
                    prediction: (!(fullPredictionData[name]) || diffDate < -1) ? null : ((diffDate > -1) ? fullPredictionData[name] : [fullPredictionData[name][fullPredictionData[name].length - 1]]),
                    color: chartColors[idx],
                    isLivePeriod: viewPeriodService.DAY_LIST_MOMENT.map(dateMoment => dateMoment.clone().isSameOrAfter(todayDate.clone(), viewPeriodService.viewPeriod.toMomentCompareString())),
                    toolTipLabel: viewPeriodService.DAY_LIST.map(dayText => `${this.getDisplayName(name)}, ${dayText}`),
                    label: this.getDisplayName(name),
                }));
                chartData$.next(lineChartData);
            }));

        const componentRef = viewContainerRef.createComponent(componentFactory);
        const comInstance = componentRef.instance as DynamicLineChartWrapperComponent;
        comInstance.isLock = this.isLock;
        comInstance.title = configDataService.DISPLAY_LANGUAGE.TRAFFIC_TREND;
        comInstance.data$ = chartData$;
        comInstance.label$ = viewPeriodService.DAY_LIST$;
        return comInstance;
    }

}
